import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import {
  MessageSquare,
  ClipboardCheck,
  Sparkles,
  Phone,
  Check,
  ArrowRight,
} from "lucide-react";
import Tilt from "@/components/site/Tilt";

const STEPS = [
  {
    n: "01",
    t: "Anfrage senden",
    d: "Kontaktieren Sie uns telefonisch oder über das Formular und schildern Sie Ihr Anliegen.",
    icon: MessageSquare,
    accent: "from-sky-400 to-sky-600",
    ring: "ring-sky-200/60",
    chipBg: "bg-sky-50",
    chipText: "text-sky-700",
    chipBorder: "border-sky-200",
    bubble: "bg-sky-500",
    duration: "≈ 2 Min.",
    side: Phone,
  },
  {
    n: "02",
    t: "Beratung erhalten",
    d: "Wir melden uns zeitnah zurück und besprechen die passende Lösung für Ihr Objekt.",
    icon: ClipboardCheck,
    accent: "from-amber-400 to-orange-500",
    ring: "ring-amber-200/60",
    chipBg: "bg-amber-50",
    chipText: "text-amber-700",
    chipBorder: "border-amber-200",
    bubble: "bg-amber-500",
    duration: "Antwort < 24 h",
    side: Check,
  },
  {
    n: "03",
    t: "Reinigung starten",
    d: "Unser Team übernimmt die Reinigung professionell, zuverlässig und termingerecht.",
    icon: Sparkles,
    accent: "from-emerald-400 to-teal-600",
    ring: "ring-emerald-200/60",
    chipBg: "bg-emerald-50",
    chipText: "text-emerald-700",
    chipBorder: "border-emerald-200",
    bubble: "bg-emerald-500",
    duration: "Termingerecht",
    side: Sparkles,
  },
];

export default function Process() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 85%", "end 30%"],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 80, damping: 20, mass: 0.3 });
  const pathDraw = useTransform(smooth, [0, 1], [0, 1]);
  const sparkleX = useTransform(smooth, [0, 1], ["0%", "100%"]);
  const sparkleScale = useTransform(smooth, [0, 0.1, 0.9, 1], [0, 1, 1, 0]);

  return (
    <section
      id="ablauf"
      data-testid="process-section"
      ref={sectionRef}
      className="relative py-24 md:py-36 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50/40"
      style={{ perspective: "1200px" }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(2,132,199,0.7) 1px,transparent 1px),linear-gradient(90deg,rgba(2,132,199,0.7) 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        className="pointer-events-none absolute top-20 right-[8%] w-2 h-2 rounded-full bg-sky-400 shadow-[0_0_20px_rgba(56,189,248,0.8)]"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.4 }}
        className="pointer-events-none absolute bottom-32 left-[12%] w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_20px_rgba(251,191,36,0.8)]"
      />
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.8 }}
        className="pointer-events-none absolute top-[40%] left-[48%] w-1 h-1 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.8)]"
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-sky-700 mb-5">
              <span className="w-8 h-px bg-sky-600/60" />
              Ablauf
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold text-slate-900 leading-[1.02] tracking-tight">
              In drei Schritten zum{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-sky-500 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">
                  glänzenden Ergebnis
                </span>
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="absolute left-0 right-0 bottom-1 h-3 bg-amber-200/60 -z-0 rounded-sm origin-left"
                />
              </span>
              .
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-base leading-relaxed md:pb-2">
            Kein Papierkram, keine unnötigen Schleifen — nur ein klarer,
            unkomplizierter Weg von der Anfrage bis zum sauberen Ergebnis.
          </p>
        </div>

        {/* Desktop: Scroll-driven curved timeline */}
        <div className="hidden md:block relative mb-10">
          <svg
            viewBox="0 0 1200 120"
            className="w-full h-24"
            preserveAspectRatio="none"
            aria-hidden
          >
            <defs>
              <linearGradient id="line-gradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="50%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M 60 90 Q 300 10 600 60 T 1140 40"
              fill="none"
              stroke="#cbd5e1"
              strokeWidth="2"
              strokeDasharray="3 8"
              strokeLinecap="round"
            />
            <motion.path
              d="M 60 90 Q 300 10 600 60 T 1140 40"
              fill="none"
              stroke="url(#line-gradient)"
              strokeWidth="3"
              strokeLinecap="round"
              style={{ pathLength: pathDraw }}
              filter="url(#glow)"
            />
            {[
              { cx: 60, cy: 90, color: "#0284c7" },
              { cx: 600, cy: 60, color: "#f59e0b" },
              { cx: 1140, cy: 40, color: "#10b981" },
            ].map((p, i) => (
              <g key={i}>
                <circle cx={p.cx} cy={p.cy} r="10" fill="white" />
                <circle cx={p.cx} cy={p.cy} r="6" fill={p.color} />
              </g>
            ))}
          </svg>
          <motion.div
            style={{ left: sparkleX, scale: sparkleScale }}
            className="pointer-events-none absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
            aria-hidden
          >
            <div className="relative w-8 h-8">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-300 to-emerald-400 blur-md opacity-90" />
              <div className="relative w-8 h-8 grid place-items-center rounded-full bg-white shadow-[0_0_30px_rgba(56,189,248,0.6)] ring-2 ring-white">
                <Sparkles className="w-4 h-4 text-sky-600" strokeWidth={2.5} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Step cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const Side = s.side;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.15, ease: "easeOut" }}
                data-testid={`process-step-${s.n}`}
                className="relative"
              >
                <Tilt max={8} scale={1.02}>
                  <div className="relative rounded-3xl bg-white/80 backdrop-blur-xl border border-white shadow-[0_20px_60px_-25px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.9)] p-8 md:p-9 h-full overflow-hidden group">
                    <span
                      className={`pointer-events-none absolute -top-6 -right-4 font-display text-[180px] leading-none font-bold tracking-tighter bg-gradient-to-br ${s.accent} bg-clip-text text-transparent opacity-10 group-hover:opacity-20 transition-opacity duration-500`}
                      aria-hidden
                    >
                      {s.n}
                    </span>

                    <div
                      className={`inline-flex items-center gap-2 rounded-full ${s.chipBg} ${s.chipText} border ${s.chipBorder} px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest`}
                      style={{ transform: "translateZ(40px)" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      Schritt {s.n}
                    </div>

                    <div className="relative mt-8 mb-7" style={{ transform: "translateZ(60px)" }}>
                      <div
                        className={`absolute inset-0 -m-4 rounded-[2rem] bg-gradient-to-br ${s.accent} opacity-20 blur-2xl group-hover:opacity-40 transition-opacity duration-500`}
                      />
                      <div
                        className={`relative w-20 h-20 rounded-[1.5rem] bg-gradient-to-br ${s.accent} grid place-items-center shadow-[0_15px_35px_-10px_rgba(2,132,199,0.4),inset_0_1px_0_rgba(255,255,255,0.3)] ring-4 ring-white transition-transform duration-500 group-hover:rotate-[-6deg] group-hover:scale-105`}
                      >
                        <Icon className="w-9 h-9 text-white drop-shadow-md" strokeWidth={2} />
                      </div>
                      <div
                        className="absolute -top-2 -right-1 w-10 h-10 rounded-2xl bg-white grid place-items-center shadow-lg ring-1 ring-slate-100 transition-transform duration-500 group-hover:translate-y-[-4px] group-hover:rotate-6"
                        style={{ transform: "translateZ(80px)" }}
                      >
                        <Side className="w-4 h-4 text-slate-800" strokeWidth={2.3} />
                      </div>
                    </div>

                    <div style={{ transform: "translateZ(30px)" }}>
                      <h3 className="font-display text-2xl md:text-[26px] font-semibold text-slate-900 tracking-tight leading-tight">
                        {s.t}
                      </h3>
                      <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
                        {s.d}
                      </p>
                    </div>

                    <div className="mt-7 pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 text-xs font-medium text-slate-500">
                        <span
                          className={`w-2 h-2 rounded-full ${s.bubble} shadow-[0_0_10px_currentColor]`}
                        />
                        {s.duration}
                      </div>
                      {i < STEPS.length - 1 && (
                        <ArrowRight className="w-4 h-4 text-slate-400 hidden md:block" />
                      )}
                    </div>

                    <div className="pointer-events-none absolute top-0 left-0 w-40 h-40 rounded-full bg-white/60 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                </Tilt>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14 md:mt-16 flex items-center justify-center"
        >
          <div className="inline-flex items-center gap-3 rounded-full bg-white border border-slate-200 shadow-sm px-5 py-3 text-sm text-slate-700">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
            Kein Papierkram, keine Verpflichtung — einfach{" "}
            <a href="#kontakt" className="font-semibold text-sky-700 hover:text-sky-900 underline decoration-sky-300 underline-offset-4">
              loslegen
            </a>
            .
          </div>
        </motion.div>
      </div>
    </section>
  );
}
